/**
 * Путь к выходу с самой таблички (план 21.09, «Маршрут в табличке»): когда
 * venue.json не дошёл, показывать нечего — кроме линии, которая приехала
 * прямо в QR параметром `&r=`. Рисуем её схемой на пустом фоне: ни карты, ни
 * подложки, ни сети не требуется, достаточно распечатанного QR.
 *
 * Честность (анти-фейк): это схема, а не карта. Масштаб подписан, подложки
 * нет, анимаций нет — человек видит направление и форму пути, а не «мы знаем,
 * где вы стоите».
 */
import type { LonLat } from '@app/geo'
import { tr } from '@app/i18n'
import { makeProjector, type SvgBox, svgPath } from '../map/svg-project'
import { langTick } from '../state'

/** Окно схемы: пропорция 3:2 удобна и в узком телефоне, и в альбомной. */
const BOX: SvgBox = { w: 360, h: 240, pad: 24 }

export interface SignRouteSchemeProps {
  /** Точки из `&r=` (decodePolyline). Меньше двух — схему не рисуем. */
  readonly points: readonly LonLat[]
}

/** Подпись у точки: держим её внутри окна, не давая уехать за край. */
function labelX(x: number): number {
  return Math.min(Math.max(x, BOX.pad), BOX.w - BOX.pad)
}

/**
 * Схема пути с таблички: линия, точка старта, маркер выхода и две подписи.
 * Вызывать, когда карта не загрузилась, а `link.r` разобран.
 */
export function SignRouteScheme({ points }: SignRouteSchemeProps) {
  langTick.value
  if (points.length < 2) return null
  const project = makeProjector(points, BOX)
  const d = svgPath(points, project)
  const first = points[0] as LonLat
  const last = points[points.length - 1] as LonLat
  const [sx, sy] = project(first)
  const [ex, ey] = project(last)
  return (
    <section class="sign-route" aria-label={tr('sign.route_title')}>
      <h2 class="sign-route-title">{tr('sign.route_title')}</h2>
      <svg
        class="sign-route-svg"
        viewBox={`0 0 ${BOX.w} ${BOX.h}`}
        role="img"
        aria-label={tr('sign.route_title')}
      >
        <path class="sign-route-line" d={d} />
        <circle class="sign-route-start" cx={sx} cy={sy} r={7} />
        <g class="sign-route-exit">
          <rect x={ex - 8} y={ey - 8} width={16} height={16} rx={4} />
          {/* Стрелка наружу — тот же знак, что на табличке выхода. */}
          <path
            class="sign-route-exit-arrow"
            d={`M${ex - 3} ${ey - 4} L${ex + 4} ${ey} L${ex - 3} ${ey + 4} Z`}
          />
        </g>
        <text class="sign-route-cap" x={labelX(sx)} y={sy + 22} text-anchor="middle">
          {tr('sign.route_start')}
        </text>
        <text class="sign-route-cap" x={labelX(ex)} y={ey - 14} text-anchor="middle">
          {tr('sign.route_exit')}
        </text>
      </svg>
      <p class="sign-route-hint">{tr('sign.route_hint')}</p>
    </section>
  )
}
